import {
  DeviceUnauthorizedDialog
} from "../chunk-EVHOV63B.js";
import "../chunk-O3EQCY62.js";
import {
  isDeviceUnauthorizedError
} from "../chunk-NVQHPHNR.js";
import "../chunk-W4TWOJ4Y.js";
import {
  ensureDeviceAuthorized,
  withVersion
} from "../chunk-UEDPR2CR.js";
import "../chunk-KVCLZREV.js";
import {
  signedFetch
} from "../chunk-2CSC3FHL.js";

// lib/login/login.tsx
import styled from "@emotion/styled";
import { Backdrop } from "@lune/common/backdrop";
import { BuildNumber } from "@lune/common/build";
import { Copyright } from "@lune/common/copyright";
import { logDebug } from "@lune/common/logger";
import { CheckUpdates } from "@lune/common/updates";
import { Theme as Theme2, Theme as ThemeContext } from "@lune/theme/context";
import { LockOutlined as LoginIcon } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  boxClasses,
  buttonClasses,
  gridClasses as gridClasses2,
  typographyClasses
} from "@mui/material";
import {
  useCallback as useCallback2,
  useContext as useContext2,
  useState
} from "react";

// lib/login/checkAuthentication.tsx
import {
  buildRedirectUri
} from "@lune/common/paths";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { jsx } from "react/jsx-runtime";
var PHASE_FAST_ENDS_MS = 30 * 1e3;
var PHASE_MEDIUM_ENDS_MS = 5 * 60 * 1e3;
var PHASE_FAST_INTERVAL_MS = 1 * 1e3;
var PHASE_MEDIUM_INTERVAL_MS = 5 * 1e3;
var PHASE_SLOW_INTERVAL_MS = 30 * 1e3;
var intervalForElapsed = (elapsedMs) => {
  if (elapsedMs < PHASE_FAST_ENDS_MS) return PHASE_FAST_INTERVAL_MS;
  if (elapsedMs < PHASE_MEDIUM_ENDS_MS) return PHASE_MEDIUM_INTERVAL_MS;
  return PHASE_SLOW_INTERVAL_MS;
};
var CheckAuthentication = ({
  version,
  loginUrl,
  rootUrl,
  loginPage
}) => {
  const navigate = useNavigate();
  const mountedAtMs = useRef(Date.now());
  const timeoutRef = useRef(null);
  const scheduleNextCheck = useCallback(
    (checkFn) => {
      const elapsedMs = Date.now() - mountedAtMs.current;
      timeoutRef.current = setTimeout(() => {
        void checkFn();
      }, intervalForElapsed(elapsedMs));
    },
    []
  );
  const check = useCallback(async () => {
    try {
      const auth = await withVersion(version, loginUrl, rootUrl);
      await auth.whoamiNotEnsured();
      navigate(rootUrl.root.url.toString());
    } catch (err) {
      if (loginPage !== void 0 && !isDeviceUnauthorizedError(err)) {
        window.location.assign(buildRedirectUri(loginPage));
      } else {
        scheduleNextCheck(check);
      }
    }
  }, [version, loginUrl, rootUrl, navigate, scheduleNextCheck, loginPage]);
  useEffect(() => {
    scheduleNextCheck(check);
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [check, scheduleNextCheck]);
  return /* @__PURE__ */ jsx(DeviceUnauthorizedDialog, {});
};

// lib/login/splash.ts
import { Theme } from "@lune/theme/context";
import { gridClasses } from "@mui/material";
import { useContext } from "react";
var useStyledSplashImage = ({
  image
}) => {
  const theme = useContext(Theme);
  return `
      height: 100vh;
      &.${gridClasses.root} {
        ${image !== void 0 ? `background-image: url(${image.toString()});` : null}  
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center;
        ${theme.mode === "dark" ? `filter: grayscale(1) brightness(0.6) contrast(2);` : null}
      }
    `;
};

// lib/login/login.tsx
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var randomImageSource = new URL("https://unsplash.it/1200/1600");
var CHECK_VERSION_BEFORE_LOGIN_MS = 5 * 60 * 1e3;
var StyledCheckUpdates = styled(CheckUpdates)`
  margin-bottom: 24px;
`;
var AppName = styled(Typography)`
  &.${typographyClasses.root} {
    font-variant: small-caps;
    margin: 1rem 0;
    display: inline-block;
  }
`;
var SigninTitle = styled(Typography)`
  &.${typographyClasses.root} {
    margin-top: 1rem;
  }
`;
var GridRoot = styled(Grid)`
  display: flex;
  flex-direction: row;
  flex: 1;
`;
var SplashImage = styled(Grid)(
  (args) => useStyledSplashImage(args)
);
var IconBox = styled.div(() => {
  const theme = useContext2(Theme2);
  return `
    display: flex;
    align-items: center;
    justify-content: center;
    width: 96px;
    height: 96px;
    ${theme.mode === "dark" ? `filter: grayscale(1);` : null}
  `;
});
var LoginForm = styled(Grid)`
  &.${gridClasses2.root} {
    margin: 4rem 2rem;
    margin-bottom: 1rem;
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
  }
`;
var Form = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%; // Fix IE 11 issue.
  margin-top: 0;
`;
var CapsLockWarning = styled.div`
  text-align: center;
  height: 1rem;
  font-size: 10pt;
`;
var SubmitButton = styled(Button)`
  &.${buttonClasses.root} {
    margin: 2rem 0;
  }
`;
var SpaceFiller = styled(Box)`
  &.${boxClasses.root} {
    flex: 1;
  }
`;
var CalloutBox = styled(Box)`
  &.${boxClasses.root} {
    margin: 2rem 0;
  }
`;
var CopyrightBox = styled(Box)`
  &.${boxClasses.root} {
    text-align: center;
  }
`;
var BuildNumberBox = styled(Box)`
  &.${boxClasses.root} {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-content: flex-end;
  }
`;
var DayOrNightBox = styled.div`
  position: absolute;
  display: flex;
  right: 12px;
  top: 12px;
  background-color: rgba(255, 255, 255, 0.2);
  height: 32px;
  width: 32px;
  border-radius: 50%;
  align-content: center;
  flex-wrap: wrap;
  justify-content: center;
`;
var InvalidUserPassAlert = styled(Alert)`
  margin: 1rem 0;
`;
var ServerErrorAlert = styled(Alert)`
  margin: 1rem 0;
`;
var UnauthorizedDeviceAlert = styled(Alert)`
  margin: 1rem 0;
`;
var NameContainer = styled.div`
  display: flex;
  align-items: center;
  justify-items: center;
  gap: 0.5rem;
`;
var LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-items: center;
  gap: 0.5rem;

  & img {
    max-width: 64px;
    max-height: 64px;
  }
`;
var resolveSuccessUri = (configured) => {
  const params = new URLSearchParams(window.location.search);
  const redirectUri = params.get("redirect_uri");
  if (redirectUri === null) return configured;
  if (redirectUri.startsWith("/") && !redirectUri.startsWith("//"))
    return redirectUri;
  try {
    if (new URL(redirectUri).origin === window.location.origin)
      return redirectUri;
  } catch {
  }
  return configured;
};
var Login = ({
  name,
  paths,
  icon,
  image,
  onSubmit,
  bottomCallout,
  showImage = true,
  versionLogo,
  appOnServerBuildGetter,
  clientBuildNumber,
  redirectOnLoggedIn
}) => {
  const themeContext = useContext2(ThemeContext);
  const [capsLockPressed, setCapsLockPressed] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [unauthorizedDevice, setUnauthorizedDevice] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const formUrl = paths.LoginApiUrl.login.api.url instanceof URL ? paths.LoginApiUrl.login.api.url : (() => {
    const result = new URL(document.location.toString());
    result.pathname = paths.LoginApiUrl.login.api.url.toString();
    return result;
  })();
  const successUri = resolveSuccessUri(
    paths.LoginSuccessUrl.login.success.url.toString()
  );
  const formOptions = new URLSearchParams({
    type: "api",
    successUri,
    failedUri: paths.LoginFailedUrl.login.failed.url.toString()
  });
  formUrl.search = formOptions.toString();
  logDebug("Login ->", formUrl.toString(), formUrl);
  const setCapsLockFn = useCallback2((e) => {
    setCapsLockPressed(e.getModifierState("CapsLock"));
  }, []);
  const resetErrors = () => {
    setServerError(false);
    setLoginFailed(false);
    setUnauthorizedDevice(false);
  };
  const postLogin = async () => {
    try {
      console.debug("Logging in", formUrl);
      const data = new URLSearchParams();
      data.append("username", username);
      data.append("password", password);
      const result = await signedFetch(formUrl, {
        method: "POST",
        body: data.toString(),
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        }
      });
      console.log("login result", result);
      return result;
    } catch (err) {
      console.error("login error", err);
      return Promise.reject("login error" + err);
    }
  };
  const handleLogin = (e) => {
    e.preventDefault();
    resetErrors();
    setSigningIn(true);
    void (async () => {
      try {
        let result;
        try {
          result = await ensureDeviceAuthorized(await postLogin());
          setUnauthorizedDevice(false);
        } catch (err) {
          if (!isDeviceUnauthorizedError(err)) {
            throw err;
          }
          console.warn("Device is not authorized");
          setUnauthorizedDevice(true);
          result = await ensureDeviceAuthorized(await postLogin());
        }
        if (!result.ok) {
          switch (result.status) {
            case 401:
              console.warn("Invalid login");
              setLoginFailed(true);
              break;
            default:
              console.warn("General failure", result.status);
              setServerError(true);
          }
        }
        if (onSubmit !== void 0) {
          onSubmit(result.ok);
        }
        if (result.ok) {
          document.location = successUri;
        } else {
          setSigningIn(false);
        }
      } catch (err) {
        console.error("login failed", err);
        setSigningIn(false);
        if (onSubmit !== void 0) {
          onSubmit(false);
        }
      }
    })();
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    redirectOnLoggedIn !== void 0 ? /* @__PURE__ */ jsx2(
      CheckAuthentication,
      {
        version: redirectOnLoggedIn.ApiVersion,
        loginUrl: redirectOnLoggedIn.LoginUrl,
        rootUrl: paths.RootUrl
      }
    ) : null,
    /* @__PURE__ */ jsxs(GridRoot, { container: true, children: [
      /* @__PURE__ */ jsx2(
        SplashImage,
        {
          image: image ?? randomImageSource,
          size: {
            xs: false,
            sm: showImage ? 4 : false,
            md: showImage ? 7 : false
          }
        }
      ),
      /* @__PURE__ */ jsxs(
        LoginForm,
        {
          size: { xs: 12, sm: showImage ? 8 : 12, md: showImage ? 5 : 12 },
          children: [
            /* @__PURE__ */ jsx2(
              StyledCheckUpdates,
              {
                checkEveryMs: CHECK_VERSION_BEFORE_LOGIN_MS,
                rootUrl: paths.RootUrl,
                appOnServerBuildGetter,
                clientBuildNumber
              }
            ),
            /* @__PURE__ */ jsx2(IconBox, { children: icon !== void 0 ? icon : /* @__PURE__ */ jsx2(Avatar, { children: /* @__PURE__ */ jsx2(LoginIcon, {}) }) }),
            name !== void 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsxs(NameContainer, { children: [
                /* @__PURE__ */ jsx2(AppName, { variant: "h4", children: name }),
                versionLogo !== void 0 && /* @__PURE__ */ jsx2(LogoContainer, { children: versionLogo })
              ] }),
              /* @__PURE__ */ jsx2(SigninTitle, { variant: "h6", children: "Sign in" })
            ] }) : /* @__PURE__ */ jsx2(SigninTitle, { variant: "h5", children: "Sign in" }),
            document.location.search === "?failed" || loginFailed ? /* @__PURE__ */ jsx2(InvalidUserPassAlert, { severity: "error", children: "Incorrect username or password!" }) : null,
            serverError ? /* @__PURE__ */ jsx2(ServerErrorAlert, { severity: "warning", children: "Failed to connect to server!" }) : null,
            unauthorizedDevice ? /* @__PURE__ */ jsx2(UnauthorizedDeviceAlert, { severity: "error", children: "Unauthorized device!" }) : null,
            /* @__PURE__ */ jsxs(Form, { onSubmit: handleLogin, children: [
              /* @__PURE__ */ jsx2(
                TextField,
                {
                  variant: "outlined",
                  margin: "normal",
                  required: true,
                  fullWidth: true,
                  id: "username",
                  label: "User Name",
                  name: "username",
                  autoFocus: true,
                  autoComplete: "off",
                  value: username,
                  onChange: (e) => {
                    setUsername(e.target.value);
                    resetErrors();
                  },
                  onKeyUp: setCapsLockFn
                }
              ),
              /* @__PURE__ */ jsx2(
                TextField,
                {
                  variant: "outlined",
                  margin: "normal",
                  required: true,
                  fullWidth: true,
                  name: "password",
                  label: "Password",
                  type: "password",
                  id: "password",
                  autoComplete: "off",
                  value: password,
                  onChange: (e) => {
                    setPassword(e.target.value);
                    resetErrors();
                  },
                  onKeyUp: setCapsLockFn
                }
              ),
              /* @__PURE__ */ jsx2(CapsLockWarning, { children: capsLockPressed ? /* @__PURE__ */ jsx2(Fragment, { children: "Caps Lock is on" }) : null }),
              /* @__PURE__ */ jsx2(
                SubmitButton,
                {
                  type: "submit",
                  fullWidth: true,
                  variant: "contained",
                  color: "primary",
                  children: "Sign In"
                }
              ),
              /* @__PURE__ */ jsx2(SpaceFiller, {}),
              /* @__PURE__ */ jsx2(CalloutBox, { children: bottomCallout }),
              /* @__PURE__ */ jsx2(CopyrightBox, { children: /* @__PURE__ */ jsx2(Copyright, {}) }),
              /* @__PURE__ */ jsx2(BuildNumberBox, { children: /* @__PURE__ */ jsx2(BuildNumber, {}) })
            ] })
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsx2(DayOrNightBox, { children: /* @__PURE__ */ jsx2(
      IconButton,
      {
        onClick: themeContext.toggle,
        onContextMenu: themeContext.reset,
        children: themeContext.icon
      }
    ) }),
    /* @__PURE__ */ jsx2(Backdrop, { open: signingIn })
  ] });
};
export {
  CheckAuthentication,
  Login
};
